/**
 * Pure ZPL string builders. No DOM, no SDK dependencies — testable in isolation.
 *
 * Conversion: dots = mm * dpi / 25.4. ZD420/ZD220 ship at 203 dpi.
 */

export interface QrLabelInput {
  qrPayload: string;
  headerText?: string;
  footerText?: string;
}

export interface BuildLabelOptions {
  widthMm: number;
  heightMm: number;
  dpi: number;
  /**
   * Optional explicit gap (mm) between the QR symbol and the *header* row
   * (e.g. container number). Falls back to the computed inner margin
   * (≈4 % of the smaller cell dimension) when omitted, preserving legacy
   * behaviour for callers that don't set it.
   */
  qrHeaderGapMm?: number;
  /**
   * Optional explicit gap (mm) between the QR symbol and the *footer* row
   * (e.g. page number). Same fallback as `qrHeaderGapMm`.
   */
  qrFooterGapMm?: number;
  /**
   * Optional explicit height (mm) for the header text row. 0 / undefined
   * falls back to the adaptive sizing pass that picks the largest font
   * fitting the QR width.
   */
  qrHeaderFontMm?: number;
  /**
   * Optional explicit height (mm) for the footer text row. Same semantics
   * as `qrHeaderFontMm`.
   */
  qrFooterFontMm?: number;
}

const QR_ERROR_CORRECTION = 'M';

function mmToDots(mm: number, dpi: number): number {
  return Math.round((mm * dpi) / 25.4);
}

// Target the QR at ~98% of the smaller usable dimension. The remaining
// 2% absorbs printer alignment slop; pushing higher would risk the QR
// being clipped on a misaligned cut. Lower values waste space — at
// integer ^BQ magnification a small bump can jump a whole step (e.g. 6→7
// at 600 dpi on a 12.7×12.7 mm label).
const QR_FILL_RATIO = 0.98;

/** Floor for header/footer font height in dots. ~1mm at 600dpi (~2.85pt). */
const MIN_TEXT_HEIGHT_DOTS = 24;
/** Average char-advance ratio for ZPL ^A0 (Helvetica-like) scalable font.
 *  Conservative — empirically the printer renders chars wider than 0.65, so
 *  0.7 gives more breathing room before overlap on long strings. */
const ZPL_AVG_CHAR_RATIO = 0.7;

/**
 * Pick a font height (dots) so that `text` fits within `maxWidth` at the
 * given character count, capped at `maxHeight` and floored at the minimum
 * legible size. When the text is too long even at the floor, the caller
 * uses ZPL's `^FB ...,1,0,...` to clip the overflow.
 */
function pickTextHeight(
  textLength: number,
  maxWidth: number,
  maxHeight: number
): number {
  const cap = Math.max(MIN_TEXT_HEIGHT_DOTS, maxHeight);
  if (textLength <= 0) return cap;
  const widthFit = Math.floor(maxWidth / (textLength * ZPL_AVG_CHAR_RATIO));
  return Math.max(MIN_TEXT_HEIGHT_DOTS, Math.min(cap, widthFit));
}

interface LayoutInputs {
  widthDots: number;
  heightDots: number;
  margin: number;
  usableTextW: number;
  headerLen: number;
  footerLen: number;
  textHeightCap: number;
  payloadLength: number;
  moduleCount: number;
  hasHeader: boolean;
  hasFooter: boolean;
}

/**
 * Pick QR magnification + per-row text heights so that:
 *   - the QR is as large as possible within the cell,
 *   - header/footer fonts fit the *QR* width (never wider than the QR),
 *   - text height floors at MIN_TEXT_HEIGHT_DOTS for legibility.
 *
 * Algorithm:
 *   Pass A — assume worst-case text height (the cap on both rows). Compute
 *           the smallest QR that layout can force.
 *   Pass B — size the real text to that QR's width; text usually shrinks
 *           below the cap for long strings.
 *   Pass C — recompute QR with the now-smaller reservation. The QR may
 *           grow; text stays at the values from B (a bigger QR can always
 *           hold the text we already sized for the smaller one).
 *
 * Single-pass refinement avoids the oscillation where a larger QR would
 * permit a larger font that would force the QR back down.
 */
function solveLayout(inputs: LayoutInputs): {
  mag: number;
  qrSize: number;
  headerHeight: number;
  footerHeight: number;
} {
  const {
    widthDots,
    heightDots,
    margin,
    usableTextW,
    headerLen,
    footerLen,
    textHeightCap,
    payloadLength,
    moduleCount,
    hasHeader,
    hasFooter
  } = inputs;

  const reserveOf = (h: number, f: number) =>
    (hasHeader ? h + margin : 0) + (hasFooter ? f + margin : 0) + margin * 2;

  // Pass A: worst-case reservation.
  const magWorst = pickMagnification(
    widthDots,
    heightDots,
    payloadLength,
    reserveOf(hasHeader ? textHeightCap : 0, hasFooter ? textHeightCap : 0)
  );
  const qrWorst = magWorst * moduleCount;

  // Pass B: size text to the worst-case QR width.
  const textW = Math.min(qrWorst, usableTextW);
  const headerHeight = hasHeader
    ? pickTextHeight(headerLen, textW, textHeightCap)
    : 0;
  const footerHeight = hasFooter
    ? pickTextHeight(footerLen, textW, textHeightCap)
    : 0;

  // Pass C: refined QR with actual reservation (>= magWorst).
  const mag = pickMagnification(
    widthDots,
    heightDots,
    payloadLength,
    reserveOf(headerHeight, footerHeight)
  );
  const qrSize = mag * moduleCount;

  return { mag, qrSize, headerHeight, footerHeight };
}

/**
 * Pick a QR magnification (1-10) that makes the symbol fit the label while
 * leaving room for header/footer text. Targets ~92% of the smaller label
 * dimension, minus the space reserved for header/footer text rows.
 */
function pickMagnification(
  widthDots: number,
  heightDots: number,
  payloadLength: number,
  reservedDots: number
): number {
  const usableW = widthDots;
  const usableH = Math.max(1, heightDots - reservedDots);
  const minDim = Math.min(usableW, usableH);
  const moduleCount = payloadLength <= 25 ? 25 : payloadLength <= 50 ? 33 : 45;
  const targetSize = minDim * QR_FILL_RATIO;
  const mag = Math.floor(targetSize / moduleCount);
  return Math.min(10, Math.max(1, mag));
}

export function buildQrLabel(
  label: QrLabelInput,
  options: BuildLabelOptions
): string {
  const widthDots = mmToDots(options.widthMm, options.dpi);
  const heightDots = mmToDots(options.heightMm, options.dpi);
  // Cap text height at 12% of label height. pickTextHeight floors at the
  // minimum legible size and shrinks below the cap when long strings need
  // to fit horizontally.
  const textHeightCap = Math.round(heightDots * 0.12);
  const margin = Math.max(
    2,
    Math.round(Math.min(widthDots, heightDots) * 0.04)
  );
  // Inner gaps between QR and the header / footer text rows. Each falls
  // back to `margin` when the caller doesn't override (preserves pre-
  // setting behaviour).
  const headerGap =
    options.qrHeaderGapMm != null
      ? Math.max(0, mmToDots(options.qrHeaderGapMm, options.dpi))
      : margin;
  const footerGap =
    options.qrFooterGapMm != null
      ? Math.max(0, mmToDots(options.qrFooterGapMm, options.dpi))
      : margin;

  const headerLen = label.headerText?.length ?? 0;
  const footerLen = label.footerText?.length ?? 0;
  const usableTextW = widthDots - margin * 2;
  const moduleCount =
    label.qrPayload.length <= 25 ? 25 : label.qrPayload.length <= 50 ? 33 : 45;

  // Iterative sizing: start with worst-case text reservation (cap on both
  // sides), pick a QR mag, then size text to fit the QR width; if that
  // freed up vertical space the QR grows on the next pass. Converges in
  // at most two iterations because mag is integer and monotonic.
  const solved = solveLayout({
    widthDots,
    heightDots,
    margin,
    usableTextW,
    headerLen,
    footerLen,
    textHeightCap,
    payloadLength: label.qrPayload.length,
    moduleCount,
    hasHeader: !!label.headerText,
    hasFooter: !!label.footerText
  });

  // Operator overrides — when an explicit font height (mm) is configured,
  // override the solver's adaptive pick. Clamp to the same cap so a
  // mis-typed huge value can't push the QR off the cell.
  const overrideHeaderHeight =
    options.qrHeaderFontMm != null && options.qrHeaderFontMm > 0
      ? Math.min(textHeightCap, mmToDots(options.qrHeaderFontMm, options.dpi))
      : null;
  const overrideFooterHeight =
    options.qrFooterFontMm != null && options.qrFooterFontMm > 0
      ? Math.min(textHeightCap, mmToDots(options.qrFooterFontMm, options.dpi))
      : null;

  const mag = solved.mag;
  const qrSize = solved.qrSize;
  const headerHeight = overrideHeaderHeight ?? solved.headerHeight;
  const footerHeight = overrideFooterHeight ?? solved.footerHeight;

  const textRenderW = Math.min(qrSize, usableTextW);

  const headerOffset = label.headerText ? headerHeight + headerGap : 0;
  const footerOffset = label.footerText ? footerGap + footerHeight : 0;

  // Cluster header → QR → footer with the configured per-side gaps and
  // centre the cluster vertically. Spare space (common on tall/narrow or
  // wide/short labels where the square QR can't grow further) goes to
  // the outer top/bottom of the label, not into a balloon between QR
  // and text — that's the gap users notice as "huge".
  const totalContentH = headerOffset + qrSize + footerOffset;
  const topPad = Math.max(margin, Math.floor((heightDots - totalContentH) / 2));

  const headerY = topPad;
  const qrY = topPad + headerOffset;
  const footerY = qrY + qrSize + footerGap;
  const qrX = Math.max(0, Math.round((widthDots - qrSize) / 2));

  const textRenderX = Math.max(
    margin,
    Math.round((widthDots - textRenderW) / 2)
  );

  const lines: string[] = [];
  lines.push('^XA');
  lines.push(`^PW${widthDots}`);
  lines.push(`^LL${heightDots}`);
  lines.push('^LH0,0');
  lines.push('^CI28'); // UTF-8

  if (label.headerText) {
    lines.push(
      `^FO${textRenderX},${headerY}^A0N,${headerHeight},${headerHeight}^FB${textRenderW},1,0,C,0^FD${escapeZpl(label.headerText)}^FS`
    );
  }

  lines.push(
    `^FO${qrX},${qrY}^BQN,2,${mag},${QR_ERROR_CORRECTION},7^FDLA,${escapeZpl(label.qrPayload)}^FS`
  );

  if (label.footerText) {
    lines.push(
      `^FO${textRenderX},${footerY}^A0N,${footerHeight},${footerHeight}^FB${textRenderW},1,0,C,0^FD${escapeZpl(label.footerText)}^FS`
    );
  }

  lines.push('^PQ1');
  lines.push('^XZ');
  return lines.join('\n');
}

export function buildBatchLabels(
  labels: QrLabelInput[],
  options: BuildLabelOptions
): string {
  return labels.map((l) => buildQrLabel(l, options)).join('\n');
}

export interface GridLayoutOptions {
  paperWidthMm: number;
  paperHeightMm: number;
  cols: number;
  rows: number;
  marginMm: number;
  gapMm: number;
  dpi: number;
  /** See BuildLabelOptions.qrHeaderGapMm — same semantics, applied per cell. */
  qrHeaderGapMm?: number;
  /** See BuildLabelOptions.qrFooterGapMm — same semantics, applied per cell. */
  qrFooterGapMm?: number;
  /** See BuildLabelOptions.qrHeaderFontMm — same semantics, applied per cell. */
  qrHeaderFontMm?: number;
  /** See BuildLabelOptions.qrFooterFontMm — same semantics, applied per cell. */
  qrFooterFontMm?: number;
}

/**
 * Build ZPL that places multiple labels in a grid on a single feed.
 *
 * Each chunk of `cols * rows` labels becomes one `^XA…^XZ` block — one feed
 * cycle on the Zebra, one piece of paper out. The paper is sized via `^PW`
 * and `^LL` to the full sheet (not the cell), and each label is rendered
 * inside its own cell using the same field structure as `buildQrLabel`,
 * just offset by `^FO{cellX},{cellY}`.
 *
 * Use this when printing onto a pre-cut sticker sheet that holds multiple
 * stickers in fixed positions (e.g. 100×75mm sheet pre-cut into 3×3 cells).
 */
export function buildGridZpl(
  labels: QrLabelInput[],
  options: GridLayoutOptions
): string {
  const cols = Math.max(1, Math.floor(options.cols));
  const rows = Math.max(1, Math.floor(options.rows));
  const cellsPerSheet = cols * rows;
  if (labels.length === 0 || cellsPerSheet === 0) {
    return '';
  }

  const paperWDots = mmToDots(options.paperWidthMm, options.dpi);
  const paperHDots = mmToDots(options.paperHeightMm, options.dpi);
  const marginDots = mmToDots(options.marginMm, options.dpi);
  const gapDots = mmToDots(options.gapMm, options.dpi);

  const cellWDots = Math.floor(
    (paperWDots - 2 * marginDots - (cols - 1) * gapDots) / cols
  );
  const cellHDots = Math.floor(
    (paperHDots - 2 * marginDots - (rows - 1) * gapDots) / rows
  );

  if (cellWDots <= 0 || cellHDots <= 0) {
    // Geometry is impossible (margin/gap too large for paper). Caller can
    // detect by seeing zero output or by validating before calling.
    return '';
  }

  const sheets: string[] = [];
  for (let start = 0; start < labels.length; start += cellsPerSheet) {
    const chunk = labels.slice(start, start + cellsPerSheet);
    const lines: string[] = [];
    lines.push('^XA');
    lines.push(`^PW${paperWDots}`);
    lines.push(`^LL${paperHDots}`);
    lines.push('^LH0,0');
    lines.push('^CI28');

    const headerGapDots =
      options.qrHeaderGapMm != null
        ? Math.max(0, mmToDots(options.qrHeaderGapMm, options.dpi))
        : undefined;
    const footerGapDots =
      options.qrFooterGapMm != null
        ? Math.max(0, mmToDots(options.qrFooterGapMm, options.dpi))
        : undefined;
    const headerFontDots =
      options.qrHeaderFontMm != null && options.qrHeaderFontMm > 0
        ? mmToDots(options.qrHeaderFontMm, options.dpi)
        : undefined;
    const footerFontDots =
      options.qrFooterFontMm != null && options.qrFooterFontMm > 0
        ? mmToDots(options.qrFooterFontMm, options.dpi)
        : undefined;
    chunk.forEach((label, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cellX = marginDots + col * (cellWDots + gapDots);
      const cellY = marginDots + row * (cellHDots + gapDots);
      lines.push(
        ...renderCellFields(
          label,
          cellX,
          cellY,
          cellWDots,
          cellHDots,
          headerGapDots,
          footerGapDots,
          headerFontDots,
          footerFontDots
        )
      );
    });

    lines.push('^PQ1');
    lines.push('^XZ');
    sheets.push(lines.join('\n'));
  }

  return sheets.join('\n');
}

/**
 * Emit ZPL field commands for a single cell at the given origin.
 * Mirrors the layout `buildQrLabel` produces but offset by (originX, originY)
 * and sized to the cell instead of the whole label.
 */
function renderCellFields(
  label: QrLabelInput,
  originX: number,
  originY: number,
  cellW: number,
  cellH: number,
  qrHeaderGapDots?: number,
  qrFooterGapDots?: number,
  qrHeaderFontDots?: number,
  qrFooterFontDots?: number
): string[] {
  const textHeightCap = Math.round(cellH * 0.12);
  const margin = Math.max(2, Math.round(Math.min(cellW, cellH) * 0.04));
  const headerGap = qrHeaderGapDots ?? margin;
  const footerGap = qrFooterGapDots ?? margin;
  const usableTextW = cellW - margin * 2;
  const moduleCount =
    label.qrPayload.length <= 25 ? 25 : label.qrPayload.length <= 50 ? 33 : 45;

  const solved = solveLayout({
    widthDots: cellW,
    heightDots: cellH,
    margin,
    usableTextW,
    headerLen: label.headerText?.length ?? 0,
    footerLen: label.footerText?.length ?? 0,
    textHeightCap,
    payloadLength: label.qrPayload.length,
    moduleCount,
    hasHeader: !!label.headerText,
    hasFooter: !!label.footerText
  });

  const mag = solved.mag;
  const qrSize = solved.qrSize;
  const headerHeight =
    qrHeaderFontDots != null && qrHeaderFontDots > 0
      ? Math.min(textHeightCap, qrHeaderFontDots)
      : solved.headerHeight;
  const footerHeight =
    qrFooterFontDots != null && qrFooterFontDots > 0
      ? Math.min(textHeightCap, qrFooterFontDots)
      : solved.footerHeight;

  const headerOffset = label.headerText ? headerHeight + headerGap : 0;
  const footerOffset = label.footerText ? footerGap + footerHeight : 0;

  // Cluster header → QR → footer with the configured per-side gaps and
  // centre the cluster vertically in the cell so spare space sits at
  // the cell's edges, not between QR and text. See the matching block
  // in buildQrLabel for why.
  const totalContentH = headerOffset + qrSize + footerOffset;
  const topPad = Math.max(margin, Math.floor((cellH - totalContentH) / 2));

  const headerY = originY + topPad;
  const qrY = originY + topPad + headerOffset;
  const footerY = qrY + qrSize + footerGap;
  const qrX = originX + Math.max(0, Math.round((cellW - qrSize) / 2));

  // Constrain text rendering to QR width — text never extends horizontally
  // beyond the QR symbol regardless of payload length.
  const textRenderW = Math.min(qrSize, usableTextW);
  const textRenderX =
    originX + Math.max(margin, Math.round((cellW - textRenderW) / 2));

  const lines: string[] = [];
  if (label.headerText) {
    lines.push(
      `^FO${textRenderX},${headerY}^A0N,${headerHeight},${headerHeight}^FB${textRenderW},1,0,C,0^FD${escapeZpl(label.headerText)}^FS`
    );
  }
  lines.push(
    `^FO${qrX},${qrY}^BQN,2,${mag},${QR_ERROR_CORRECTION},7^FDLA,${escapeZpl(label.qrPayload)}^FS`
  );
  if (label.footerText) {
    lines.push(
      `^FO${textRenderX},${footerY}^A0N,${footerHeight},${footerHeight}^FB${textRenderW},1,0,C,0^FD${escapeZpl(label.footerText)}^FS`
    );
  }
  return lines;
}

function escapeZpl(text: string): string {
  return text.replace(/[\^~\\]/g, ' ');
}
